/**
 * The Compare rows card's working copy, and how it turns into what is saved. Pure, so the card
 * only holds state and wires events.
 */

import type { CompareRow, CompareRowSource } from './types';

export type SourceKind = CompareRowSource['kind'];

/** One source as edited: every kind's fields kept, so switching kind back loses nothing. */
export interface SourceDraft {
	kind: SourceKind;
	name: string;
	tags: string[];
}

export interface RowDraft {
	/** Local identity for React keys. Never sent. */
	id: string;
	label: string;
	productTypes: string[];
	sources: SourceDraft[];
}

export const SOURCE_KINDS: { kind: SourceKind; label: string }[] = [
	{ kind: 'price', label: 'Price' },
	{ kind: 'option', label: 'Shopify option' },
	{ kind: 'tags', label: 'Tags' },
	{ kind: 'description', label: 'Description' }
];

let rowSequence = 0;

export const emptySource = (kind: SourceKind = 'tags'): SourceDraft => ({
	kind,
	name: '',
	tags: []
});

export const toRowDraft = (row?: CompareRow): RowDraft => {
	rowSequence += 1;
	return {
		id: `compare-row-${rowSequence}`,
		label: row?.label ?? '',
		productTypes: [...(row?.productTypes ?? [])],
		sources: row?.sources.length
			? row.sources.map((source) => ({
					kind: source.kind,
					name: source.kind === 'option' ? source.name : '',
					tags: source.kind === 'tags' ? [...source.tags] : []
				}))
			: [emptySource()]
	};
};

const toSource = (source: SourceDraft): CompareRowSource => {
	if (source.kind === 'option') return { kind: 'option', name: source.name.trim() };
	if (source.kind === 'tags') return { kind: 'tags', tags: source.tags };
	return { kind: source.kind };
};

/** What a save sends. */
export const toCompareRows = (rows: RowDraft[]): CompareRow[] =>
	rows.map((row) => ({
		label: row.label.trim(),
		productTypes: row.productTypes,
		sources: row.sources.map(toSource)
	}));

/** Why the rows cannot be saved yet, or null. Mirrors the API's checks. */
export const rowsProblem = (rows: RowDraft[]): string | null => {
	const labels = rows.map((row) => row.label.trim().toLowerCase());
	if (labels.some((label) => !label)) return 'Give every row a label.';
	if (new Set(labels).size !== labels.length) return 'Each row needs its own label.';
	for (const row of rows) {
		if (row.sources.some((source) => source.kind === 'option' && !source.name.trim()))
			return `Name the Shopify option for “${row.label.trim()}”.`;
		if (row.sources.some((source) => source.kind === 'tags' && source.tags.length === 0))
			return `Pick at least one tag for “${row.label.trim()}”.`;
	}
	return null;
};

export const sameRows = (a: CompareRow[], b: CompareRow[]): boolean => JSON.stringify(a) === JSON.stringify(b);
